import { RefObject, useEffect } from 'react';
import {
  findInlineTarget,
  focusNavCell,
  getEditableNavCells,
  INLINE_CELL_SELECTOR,
  isDropdownOpen,
  type InlineMoveDirection,
} from './inlineCellNavigation';

const ARROW_DIRECTION: Record<string, InlineMoveDirection> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
};

export const useInlineCellNavigation = (
  rootRef: RefObject<HTMLElement | null>,
  enabled = true,
) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const current = (
        event.target as HTMLElement | null
      )?.closest<HTMLElement>(INLINE_CELL_SELECTOR);
      if (!current || !root.contains(current)) return;

      if (event.key === 'Tab') {
        event.preventDefault();
        const target = findInlineTarget(
          getEditableNavCells(root),
          current,
          event.shiftKey ? 'prev' : 'next',
        );
        if (target !== current) focusNavCell(target);
        return;
      }

      const direction = ARROW_DIRECTION[event.key];
      if (!direction || event.altKey) return;
      if (isDropdownOpen(event.target)) return;

      event.preventDefault();
      const target = findInlineTarget(
        getEditableNavCells(root),
        current,
        direction,
      );
      if (target !== current) focusNavCell(target);
    };

    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [enabled, rootRef]);
};
